/**
 * ComplexRole - Classe base abstrata para roles com variantes heterogêneas
 *
 * Para roles onde cada variante tem tipo/estrutura diferente.
 * Cada variante é implementada como sua própria classe (IVariant).
 *
 * Roles que estendem: Color, Date, etc.
 *
 * @example
 * class ColorRole extends ComplexRole<RGBA> {
 *   readonly name = 'color';
 *   readonly base = 'rgb_object';
 *
 *   protected createVariants() {
 *     return {
 *       'hex': new HexVariant(),
 *       'rgb_object': new RgbObjectVariant(),
 *       'rgb_string': new RgbStringVariant(),
 *       'hsl_object': new HslObjectVariant(),
 *     };
 *   }
 * }
 */

import type {
  RoleSpec,
  VariantSpec,
  ValidationResult,
  BaseFormatOptions,
} from "../types";
import type { IRole, Result } from "./IRole";
import type { IVariant } from "./IVariant";

// =============================================================================
// COMPLEX ROLE CLASS
// =============================================================================

/**
 * Classe base abstrata para roles com variantes heterogêneas.
 *
 * Subclasses devem definir:
 * - name: Nome da role
 * - base: Variante base
 * - createVariants(): Factory que retorna mapa de variantes
 *
 * @typeParam TBase - Tipo do valor na variante base
 */
export abstract class ComplexRole<TBase = unknown> implements IRole<TBase> {
  // ===========================================================================
  // ABSTRACT PROPERTIES (subclasses devem implementar)
  // ===========================================================================

  /** Nome único da role */
  abstract readonly name: string;

  /** Nome da variante base */
  abstract readonly base: string;

  // ===========================================================================
  // VARIANTS
  // ===========================================================================

  /** Cache das variantes */
  private _variants: Map<string, IVariant<unknown, TBase>> | null = null;

  /**
   * Factory method para criar variantes.
   * Subclasses devem implementar.
   */
  protected abstract createVariants(): Record<string, IVariant<unknown, TBase>>;

  /**
   * Retorna mapa de variantes (lazy initialization)
   */
  protected get variants(): Map<string, IVariant<unknown, TBase>> {
    if (!this._variants) {
      this._variants = new Map(Object.entries(this.createVariants()));
    }
    return this._variants;
  }

  /**
   * Retorna uma variante específica
   */
  protected getVariant(name: string): IVariant<unknown, TBase> {
    const variant = this.variants.get(name);
    if (!variant) {
      throw new Error(`Unknown variant: ${name}`);
    }
    return variant;
  }

  // ===========================================================================
  // INTROSPECTION
  // ===========================================================================

  getVariants(): string[] {
    return Array.from(this.variants.keys());
  }

  hasVariant(variant: string): boolean {
    return this.variants.has(variant);
  }

  // ===========================================================================
  // CONVERT (Pilar 1)
  // ===========================================================================

  convert<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): TTo {
    // Otimização: mesma variante
    if (from === to) {
      return value as unknown as TTo;
    }

    const fromVariant = this.getVariant(from);
    const toVariant = this.getVariant(to);

    // from → base → to
    const baseValue = fromVariant.toBase(value);
    return toVariant.fromBase(baseValue) as unknown as TTo;
  }

  tryConvert<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): Result<TTo> {
    try {
      if (!this.hasVariant(from)) {
        return { ok: false, error: `Unknown variant: ${from}` };
      }
      if (!this.hasVariant(to)) {
        return { ok: false, error: `Unknown variant: ${to}` };
      }
      const result = this.convert<TFrom, TTo>(from, to, value);
      return { ok: true, value: result };
    } catch (err) {
      return { ok: false, error: String(err) };
    }
  }

  toBase<T = unknown>(variant: string, value: T): TBase {
    return this.getVariant(variant).toBase(value);
  }

  fromBase<T = unknown>(variant: string, baseValue: TBase): T {
    return this.getVariant(variant).fromBase(baseValue) as T;
  }

  // ===========================================================================
  // CAST (Pilar 2)
  // ===========================================================================

  cast<T = unknown>(variant: string, input: unknown): T | null {
    return this.getVariant(variant).cast(input) as T | null;
  }

  tryCast<T = unknown>(variant: string, input: unknown): Result<T> {
    const result = this.cast<T>(variant, input);
    if (result === null) {
      return {
        ok: false,
        error: `Cannot cast "${String(input)}" to ${this.name}:${variant}`,
      };
    }
    return { ok: true, value: result };
  }

  // ===========================================================================
  // VALIDATE (Pilar 3)
  // ===========================================================================

  validate(variant: string, value: unknown): ValidationResult {
    return this.getVariant(variant).validate(value);
  }

  isValid(variant: string, value: unknown): boolean {
    return this.validate(variant, value).valid;
  }

  // ===========================================================================
  // FORMAT (Pilar 4)
  // ===========================================================================

  format(variant: string, value: unknown, options?: BaseFormatOptions): string {
    return this.getVariant(variant).format(value, options);
  }

  // ===========================================================================
  // COMPATIBILITY
  // ===========================================================================

  toSpec(): RoleSpec<TBase> {
    const variants: Record<string, VariantSpec<unknown, TBase>> = {};

    for (const [name, variant] of this.variants.entries()) {
      variants[name] = {
        type: variant.type,
        toBase: (v: unknown) => variant.toBase(v),
        fromBase: (v: TBase) => variant.fromBase(v),
        cast: (input: unknown) => variant.cast(input),
        validate: {
          custom: (v: unknown) => {
            const result = variant.validate(v);
            return result.valid ? null : result.errors[0];
          },
        },
        format: {
          symbol: name,
          formatter: (v: unknown, opts?: BaseFormatOptions) =>
            variant.format(v, opts),
        },
      };
    }

    return {
      base: this.base,
      variants,
    };
  }
}
